import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { format, isToday, isTomorrow, parseISO } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { Calendar, Clock, ChevronRight, Users, Check, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const COLORS = {
  green: 'bg-[#10B981]',
  blue: 'bg-[#3B82F6]',
  red: 'bg-[#EF4444]',
  purple: 'bg-[#8B5CF6]',
  orange: 'bg-[#F97316]'
};

const UpcomingEventCard = () => {
  const { getAuthHeader, user } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [nextEvent, setNextEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rsvping, setRsvping] = useState(false);

  const getLocale = () => i18n.language === 'de' ? de : enUS;

  const fetchNextEvent = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/events/upcoming?limit=1`, {
        headers: getAuthHeader()
      });
      if (response.data && response.data.length > 0) {
        setNextEvent(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch upcoming event:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (user) {
      fetchNextEvent();
    }
  }, [user, fetchNextEvent]);

  const handleRSVP = async (e, status) => {
    e.stopPropagation();
    setRsvping(true);
    try {
      await axios.post(
        `${API_URL}/events/${nextEvent.id}/rsvp`,
        { status },
        { headers: getAuthHeader() }
      );
      toast.success(status === 'accepted' ? t('events.rsvpAccepted') : t('events.rsvpDeclined'));
      fetchNextEvent();
    } catch (error) {
      toast.error(t('events.error'));
    } finally {
      setRsvping(false);
    }
  };

  if (loading || !nextEvent) {
    return null;
  }

  const eventDate = parseISO(nextEvent.date);
  const colorClass = COLORS[nextEvent.color] || COLORS.green;

  let dateLabel = format(eventDate, 'EEE, d. MMM', { locale: getLocale() });
  if (isToday(eventDate)) {
    dateLabel = t('events.today');
  } else if (isTomorrow(eventDate)) {
    dateLabel = t('events.tomorrow');
  }

  const participants = Array.isArray(nextEvent.participants) ? nextEvent.participants : [];
  const myParticipation = participants.find(p => p.user_id === user?.id);
  const acceptedCount = participants.filter(p => p.status === 'accepted').length;

  return (
    <div
      data-testid="upcoming-event-card"
      onClick={() => navigate('/plan')}
      className="bg-white rounded-2xl border border-zinc-200 p-4 cursor-pointer hover:shadow-md transition-all group"
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl ${colorClass} flex items-center justify-center text-white flex-shrink-0`}>
          <Calendar className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-xs font-medium text-zinc-500">{t('events.upcoming')}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${colorClass} text-white`}>
              {dateLabel}
            </span>
          </div>
          <h4 className="font-semibold text-zinc-900 truncate text-sm">{nextEvent.title}</h4>
          <div className="flex items-center gap-3 text-xs text-zinc-500 mt-1 flex-wrap">
            {!nextEvent.all_day && nextEvent.start_time && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {nextEvent.start_time}{nextEvent.end_time ? ` - ${nextEvent.end_time}` : ''}
              </span>
            )}
            {participants.length > 0 && (
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" />
                {acceptedCount}/{participants.length} {t('events.accepted')}
              </span>
            )}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-zinc-400 group-hover:text-zinc-600 transition-colors flex-shrink-0" />
      </div>

      {/* RSVP for invited users */}
      {myParticipation && myParticipation.status === 'pending' && (
        <div className="flex gap-2 mt-3 pt-3 border-t border-zinc-100">
          <Button
            variant="outline"
            size="sm"
            disabled={rsvping}
            onClick={(e) => handleRSVP(e, 'accepted')}
            data-testid="upcoming-event-accept-btn"
            className="flex-1 rounded-full text-[#047857] border-[#047857] hover:bg-[#D1FAE5]"
          >
            <Check className="w-4 h-4 mr-1" />
            {t('events.accept')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={rsvping}
            onClick={(e) => handleRSVP(e, 'declined')}
            data-testid="upcoming-event-decline-btn"
            className="flex-1 rounded-full text-[#D93025] border-[#D93025] hover:bg-[#FEE2E2]"
          >
            <XCircle className="w-4 h-4 mr-1" />
            {t('events.decline')}
          </Button>
        </div>
      )}

      {myParticipation && myParticipation.status !== 'pending' && (
        <div className="mt-2 pt-2 border-t border-zinc-100 text-xs">
          <span
            className={`inline-flex items-center gap-1 font-medium ${
              myParticipation.status === 'accepted' ? 'text-[#047857]' : 'text-[#D93025]'
            }`}
          >
            {myParticipation.status === 'accepted' ? <Check className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
            {t(`events.rsvp.${myParticipation.status}`)}
          </span>
        </div>
      )}
    </div>
  );
};

export default UpcomingEventCard;
